/**
 * Visual Config — ECharts option overlay pipeline.
 *
 * Entry point: `applyChartStyle(option, style, isDark, fieldMappings, containerWidth, containerHeight)`
 *
 * Called after `buildOption()` to layer Visual Editor settings on top of
 * the base chart configuration. Each transform is a pure function that
 * receives an ECharts option and returns a new one.
 *
 * Pipeline order matters — later stages may depend on earlier ones:
 *   colors → legend → tooltip → axis → chartOverrides → labels → stacking → breakdownSort → layout
 *
 * @see VISUAL_EDITOR_SPEC.md for the full capabilities matrix.
 */
import type { EChartsOption } from 'echarts';
import type { FieldMappingMap } from '../../../../../api/types';
import type { ChartStyleState, TypeStyle, ChartTypeOption } from '../../../../edit/visualConfig';
import { applyColors } from './colors';
import { applyLegend } from './legend';
import { applyTooltip } from './tooltip';
import { applyAxis } from './axis';
import { applyChartOverrides } from './chartOverrides';
import { applyLabels } from './labels';
import { applyShowTotal, applyPercentStacking } from './stacking';
import { applyBreakdownSort } from './breakdownSort';
import { applyLayout } from './layout';

/**
 * Apply all Visual Editor overrides to a built ECharts option.
 *
 * @param option          - Base ECharts option from `buildOption()`
 * @param style           - Current ChartStyleState from the editor state
 * @param isDark          - Dark mode flag (affects palette colors)
 * @param fieldMappings   - Optional field mappings for axis label resolution
 * @param containerWidth  - Container width in px (for data-aware layout)
 * @param containerHeight - Container height in px (for data-aware layout)
 */
export function applyChartStyle(
  option: EChartsOption,
  style: ChartStyleState,
  isDark: boolean,
  fieldMappings?: FieldMappingMap | null,
  containerWidth?: number,
  containerHeight?: number,
  chartType?: ChartTypeOption,
): EChartsOption {
  const ts: TypeStyle = style.typeStyle ?? {};
  // Derive chartType from option if not provided
  const ct = chartType
    ?? (Array.isArray(option.series) && option.series.length > 0 ? (option.series[0] as Record<string, unknown>).type as string : undefined)
    ?? 'bar';

  let result = { ...option };
  result = applyColors(result, style, isDark);
  result = applyLegend(result, style, ct);
  result = applyTooltip(result, style);
  result = applyAxis(result, style, ts, fieldMappings, ct);
  result = applyChartOverrides(result, style, ts, ct);
  result = applyLabels(result, style);
  result = applyShowTotal(result, style, ts);
  result = applyPercentStacking(result, ts);
  result = applyBreakdownSort(result, style, ct);

  // Layout runs LAST — computes grid/center/radius based on final
  // legend visibility, label positions, and axis titles.
  // Uses data-aware text measurement for near-pixel-perfect spacing.
  result = applyLayout(result, style, containerWidth, containerHeight, ct);

  return result;
}